import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { apply, form, maxLength, required, validateTree } from '@angular/forms/signals';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import type { ContactUsRequest } from '@mapa/esm';
import { firstValueFrom } from 'rxjs';
import { ContactUsApi } from '../../api/contact-us.api';
import { FORM_IMPORTS } from '../../forms/form-imports';
import { applyEmailSchema } from '../../forms/validators/email';
import { generateLoadingState } from '../../state/loading';
import { ImportsModule } from '../imports.module';
import { SnackbarComponent } from '../notification/snackbar';
import { getCombinedNumber, phoneNumberWithCodeSchema } from '../phone-code';
import { PhoneNumberInputComponent } from '../phone-number-input/phone-number-input.component';
import { ContactFormAcknowledgementDialog } from './acknowledgement.dialog';

@Component({
  selector: 'contact-form',
  templateUrl: './contact-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ImportsModule,
    FORM_IMPORTS,
    PhoneNumberInputComponent
  ]
})
export class ContactFormComponent {
  private readonly snackbar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly api = {
    contactUs: inject(ContactUsApi)
  } as const;

  protected readonly loading = generateLoadingState<[
    'countries',
    'submitting'
  ]>();

  protected readonly contact = (() => {
    const emptyValue = () => ({
      name: '',
      email: '',
      phoneNumber: {
        code: '',
        number: ''
      },
      subject: '',
      message: ''
    });

    const value$ = signal(emptyValue());

    const form$ = form(value$, p => {
      required(p.name, { message: 'Required' });
      maxLength(p.name, 64);

      applyEmailSchema(p.email);
      maxLength(p.email, 96);

      apply(p.phoneNumber, phoneNumberWithCodeSchema);

      validateTree(p, ({ value, fieldTreeOf }) => {
        const v = value();
        const email = fieldTreeOf(p.email);
        const phone = fieldTreeOf(p.phoneNumber);

        const isRequired = !v.email && !v.phoneNumber.code && !v.phoneNumber.number;
        if (isRequired) {
          const message = 'Either email or phone number is required';
          return [
            {
              kind: 'required',
              message,
              fieldTree: email
            },
            {
              kind: 'required',
              message,
              fieldTree: phone
            },
          ];
        }

        return null;
      });

      required(p.subject, { message: 'Required' });
      maxLength(p.subject, 200);

      maxLength(p.message, 2000);
    }, {
      name: 'contact-form',
      submission: {
        action: async (form) => {

          this.loading.add('submitting');

          try {
            const v = form().value();

            const input: ContactUsRequest = {
              name: v.name,
              email: v.email || null,
              phone: getCombinedNumber(v.phoneNumber),
              subject: v.subject,
              message: v.message || null
            };

            await firstValueFrom(this.api.contactUs.create(input));

            form().reset(emptyValue());
            ContactFormAcknowledgementDialog.open(this.dialog);

            return null;
          }
          catch (err) {
            console.error(err);
            SnackbarComponent.forError(this.snackbar, err);
            return null;
          }
          finally {
            this.loading.delete('submitting');
          }

        }
      }
    });

    this.loading.react('submitting', form$().submitting);

    return {
      form: form$,
      submitting: this.loading.has('submitting')
    } as const;
  })();
}
